import { useRef, useState } from "react";
import {
  ApplicationDocumentsToSign,
  signDocumentsByAppId,
} from "../api/signDocuments";
import { message, notification } from "antd";
import { Response } from "../../../../store/api/apiCore";
import { useGetApplicationDataToBankQuery } from "../../../../store/api/applicationBankApi/applicationBank.api";

export type ModalView = "confirmText" | "selectSign" | "ecpAnimation";

/**
 * Логика модалки подписания
 * @param isShowDefault - открыта ли модалка по-умолчанию
 * @returns {Object} Стор для модалки подписания
 * @property {boolean} isModalVisible - открыта ли модалка
 * @property {string} modalState - текущая вкладка модалки (Пользовательское соглашение, выбор подписи, процесс подписания с анимацией)
 * @property {Object} modalHandler - функции обработчики событий модалки
 */

export const useClientSignModalState = (
  isShowDefault = false,
  applicationBankId: number
) => {
  const abortRef = useRef(false); // реф для отмены подписания (если true, подписание прерывается)

  const [isModalVisible, setIsModalVisible] = useState<boolean>(isShowDefault);
  const [modalView, setModalView] = useState<ModalView>("confirmText");

  const { refetch } = useGetApplicationDataToBankQuery(applicationBankId);

  const modalHandler = {
    open: () => {
      setIsModalVisible(true);
    },
    close: () => {
      setIsModalVisible(false);
      setModalView("confirmText");
      abortRef.current = true;
    },
    // Смена страницы модалки
    setView: (view: ModalView) => {
      setModalView(view);
    },
    // Подписание документов всех заявок
    signAllDocuments: async (
      docsData: Response<ApplicationDocumentsToSign[]>,
      thumbprint: string
    ) => {
      setModalView("ecpAnimation");
      try {
        for await (const docData of docsData.data) {
          if (abortRef.current) {
            break;
          }

          await signDocumentsByAppId(
            docData.application_bank_id,
            thumbprint,
            abortRef
          );
        }
        if (!abortRef.current) {
          // message.success("Документы успешно подписаны");
          notification.success({
            message: "Документы подписаны успешно",
            duration: 0,
          });
          refetch();
        }
        modalHandler.close();
      } catch (error) {
        modalHandler.close();
        message.error("Подписание остановлено");
      }
    },
    // Подписание документов одной заявки
    signDocumentsByAppId: async (applicationId: number, thumbprint: string) => {
      setModalView("ecpAnimation");
      try {
        await signDocumentsByAppId(applicationId, thumbprint, abortRef);
        if (!abortRef.current) {
          // message.success("Документы успешно подписаны");
          notification.success({
            message: "Документы подписаны успешно",
            duration: 0,
          });
          refetch();
        }
        modalHandler.close();
      } catch (error) {
        modalHandler.close();
        message.error("Подписание остановлено");
      }
    },
    // Отмена подписания
    cancel: () => {
      if (modalView === "ecpAnimation") {
        const confrim = confirm(
          "Вы уверены, что хотите остановить подписание документов?"
        );

        if (confrim === true) {
          modalHandler.close();
          abortRef.current = true;
        }
      } else {
        modalHandler.close();
        abortRef.current = true;
      }
    },
  };

  return {
    modalView,
    isModalVisible,
    modalHandler,
  };
};
